import { useEffect, useState } from "react";
import { Activity, Download, Gamepad2, ShieldCheck, SlidersHorizontal } from "lucide-react";
import {
    RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer,
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { PageHeader } from "@/components/common/PageHeader";
import { ScoreGauge } from "@/components/common/ScoreGauge";
import { Loader } from "@/components/common/Loader";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, API } from "@/lib/api";

const CATS = [
    ["aim", "Aim"], ["positioning", "Positioning"], ["awareness", "Awareness"],
    ["movement", "Movement"], ["decision_making", "Decision Making"],
    ["survivability", "Survivability"], ["consistency", "Consistency"],
];

export default function Improvement() {
    const [data, setData] = useState(null);
    const [controller, setController] = useState(null);
    const [range, setRange] = useState("daily");

    useEffect(() => {
        api.get("/improvement").then(({ data }) => setData(data));
        api.get("/controller/profile").then(({ data }) => setController(data)).catch(() => setController({ latest: null, history: [] }));
    }, []);
    if (!data) return <Loader label="Loading improvement score" />;

    const series = data[range] || [];
    const tip = { background: "#1A1A1A", border: "1px solid #333", borderRadius: 2, fontSize: 12 };

    return (
        <div className="ww-fade-up">
            <PageHeader title="Personal Improvement Score" subtitle="Your skill profile across seven dimensions, tracked over time." />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="bg-surface border border-border rounded-sm p-6 flex flex-col items-center justify-center">
                    <ScoreGauge value={data.current?.overall || 0} size={160} label="OVERALL" />
                    <div className="flex gap-6 mt-5">
                        {[["Daily", data.deltas?.daily], ["Weekly", data.deltas?.weekly], ["Monthly", data.deltas?.monthly]].map(([k, v]) => (
                            <div key={k} className="text-center">
                                <div className={`font-mono text-sm ${v >= 0 ? "text-success" : "text-alert"}`}>{v >= 0 ? "+" : ""}{v}</div>
                                <div className="text-[10px] text-muted-foreground uppercase">{k}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="lg:col-span-2 bg-surface border border-border rounded-sm p-6">
                    <h3 className="font-heading font-bold mb-2">Skill Radar</h3>
                    <div className="h-[280px]">
                        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={280}>
                            <RadarChart data={data.radar}>
                                <PolarGrid stroke="#333" />
                                <PolarAngleAxis dataKey="category" tick={{ fill: "#A3A3A3", fontSize: 11 }} />
                                <Radar dataKey="value" stroke="#00C2FF" fill="#00C2FF" fillOpacity={0.25} strokeWidth={2} />
                                <Tooltip contentStyle={tip} />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-surface border border-border rounded-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading font-bold">Trend</h3>
                        <Tabs value={range} onValueChange={setRange}>
                            <TabsList className="bg-obsidian border border-border rounded-sm h-9">
                                <TabsTrigger value="daily" data-testid="trend-daily" className="data-[state=active]:bg-volt data-[state=active]:text-black rounded-sm text-xs">Daily</TabsTrigger>
                                <TabsTrigger value="weekly" data-testid="trend-weekly" className="data-[state=active]:bg-volt data-[state=active]:text-black rounded-sm text-xs">Weekly</TabsTrigger>
                                <TabsTrigger value="monthly" data-testid="trend-monthly" className="data-[state=active]:bg-volt data-[state=active]:text-black rounded-sm text-xs">Monthly</TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </div>
                    <div className="h-[260px]">
                        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={260}>
                            <LineChart data={series}>
                                <CartesianGrid stroke="#333" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 10 }} tickFormatter={(d) => String(d).slice(5)} minTickGap={24} />
                                <YAxis domain={[30, 100]} tick={{ fill: "#666", fontSize: 10 }} width={28} />
                                <Tooltip contentStyle={tip} />
                                <Line type="monotone" dataKey="overall" stroke="#00C2FF" strokeWidth={2.5} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-surface border border-border rounded-sm p-6">
                    <h3 className="font-heading font-bold mb-4">Categories</h3>
                    <div className="space-y-3.5">
                        {CATS.map(([key, label]) => {
                            const v = data.current?.[key] || 0;
                            const color = v >= 75 ? "#00E676" : v >= 50 ? "#00C2FF" : "#FFB000";
                            return (
                                <div key={key} data-testid={`cat-${key}`}>
                                    <div className="flex justify-between text-xs mb-1">
                                        <span className="text-muted-foreground">{label}</span>
                                        <span className="font-mono text-white">{v}</span>
                                    </div>
                                    <div className="h-1.5 bg-obsidian rounded-full overflow-hidden">
                                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${v}%`, background: color }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <ControllerResearch profile={controller} tip={tip} />
        </div>
    );
}

function ControllerResearch({ profile, tip }) {
    const [desktop, setDesktop] = useState(null);
    useEffect(() => {
        api.get("/config").then(({ data }) => setDesktop(data)).catch(() => setDesktop({ desktop_available: false }));
    }, []);
    const latest = profile?.latest;
    const detected = latest?.detection_status !== "not_detected";
    const scoredSession = detected ? latest : profile?.latest_valid;
    const scores = scoredSession?.scores || {};
    const scoreRows = [
        ["smoothness", "Stick Smoothness"], ["centering", "Aim Centering"],
        ["control", "Deflection Control"], ["precision", "Micro Precision"],
        ["reaction", "Response Timing"],
    ].filter(([key]) => scores[key] != null);

    return (
        <section className="mt-6 bg-surface border border-border rounded-sm overflow-hidden" data-testid="controller-research-panel">
            <div className="p-5 border-b border-border flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-volt/10 border border-volt/30 grid place-items-center shrink-0">
                    <Gamepad2 className="w-5 h-5 text-volt" />
                </div>
                <div className="flex-1">
                    <h3 className="font-heading font-bold">Controller Performance Research</h3>
                    <p className="text-xs text-muted-foreground mt-1">Automatically collected with Live Capture. Analysis is passive and never injects controller inputs.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-success">
                        <ShieldCheck className="w-4 h-4" /> Read-only mode
                    </div>
                    {desktop?.desktop_available && desktop?.desktop_version === "1.1.0" && (
                        <a
                            href={desktop.desktop_download_url || `${API}/desktop/download`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="controller-bridge-download"
                            className="h-9 px-3 rounded-sm bg-volt text-black text-xs font-semibold hover:bg-[#33ceff] transition-colors flex items-center gap-2"
                        >
                            <Download className="w-4 h-4" /> Download Controller Bridge (Windows Beta)
                        </a>
                    )}
                </div>
            </div>

            {!profile ? (
                <div className="p-8 text-sm text-muted-foreground">Loading controller research…</div>
            ) : !latest ? (
                <div className="p-8 flex items-start gap-3">
                    <Activity className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                        <div className="font-semibold text-white">Awaiting your first controller session</div>
                        <p className="text-sm text-muted-foreground mt-1">Connect a controller and start Live Capture. Collection starts automatically and the report appears here after monitoring stops.</p>
                    </div>
                </div>
            ) : !detected ? (
                <div className="p-8 flex items-start gap-3" data-testid="controller-not-detected">
                    <Activity className="w-5 h-5 text-alert mt-0.5" />
                    <div>
                        <div className="font-semibold text-white">Controller was not detected during the last capture</div>
                        <p className="text-sm text-muted-foreground mt-1">
                            Chrome recorded {latest.sample_count || 0} controller samples. Before the next match, keep this tab visible and press any controller button once after clicking Start Monitoring.
                        </p>
                        <p className="text-xs text-muted-foreground mt-2 font-mono">
                            Status: {latest.failure_reason || "browser_gamepad_not_detected"} · {latest.sampling_gaps || 0} gaps
                        </p>
                    </div>
                </div>
            ) : (
                <div className="p-5 grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="border border-border bg-obsidian/50 rounded-sm p-5 flex flex-col items-center justify-center">
                        <ScoreGauge value={scoredSession?.overall || 0} size={132} label="CONTROL" />
                        <div className="mt-4 text-center">
                            <div className="text-sm font-semibold text-white truncate max-w-[250px]">{scoredSession?.controller_id || "Standard controller"}</div>
                            <div className="text-xs text-muted-foreground mt-1">{scoredSession?.sample_count?.toLocaleString()} samples · {Math.round((scoredSession?.duration_sec || 0) / 60)} min</div>
                        </div>
                    </div>

                    <div className="border border-border bg-obsidian/50 rounded-sm p-5">
                        <h4 className="text-sm font-semibold mb-4">Controller dimensions</h4>
                        <div className="space-y-3.5">
                            {scoreRows.map(([key, label]) => (
                                <div key={key}>
                                    <div className="flex justify-between text-xs mb-1">
                                        <span className="text-muted-foreground">{label}</span>
                                        <span className="font-mono text-white">{scores[key]}</span>
                                    </div>
                                    <div className="h-1.5 bg-surface rounded-full overflow-hidden">
                                        <div className="h-full bg-volt rounded-full" style={{ width: `${scores[key]}%` }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="border border-border bg-obsidian/50 rounded-sm p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <SlidersHorizontal className="w-4 h-4 text-volt" />
                            <h4 className="text-sm font-semibold">Recommended adjustments</h4>
                        </div>
                        <div className="space-y-3">
                            {(scoredSession?.recommendations || []).map((item, index) => (
                                <div key={`${item.setting}-${index}`} className="border-l-2 border-volt pl-3">
                                    <div className="text-xs font-semibold text-white">{item.setting}: {item.direction} {item.amount}</div>
                                    <p className="text-[11px] leading-relaxed text-muted-foreground mt-1">{item.reason}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {profile.history?.length > 1 && (
                        <div className="xl:col-span-3 border border-border bg-obsidian/50 rounded-sm p-5">
                            <h4 className="text-sm font-semibold mb-3">Controller performance trend</h4>
                            <div className="h-[180px]">
                                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={180}>
                                    <LineChart data={profile.history}>
                                        <CartesianGrid stroke="#333" strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 10 }} />
                                        <YAxis domain={[0, 100]} tick={{ fill: "#666", fontSize: 10 }} width={28} />
                                        <Tooltip contentStyle={tip} />
                                        <Line type="monotone" dataKey="overall" stroke="#00C2FF" strokeWidth={2.5} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
